/**
 * Assessment Cycles Service
 * Handles CRUD operations for assessment cycles (historical data import periods)
 */

import { apiClient, get, post, put, del } from '@/lib/api-client';
import { cacheCycle, getCachedCycle, getCachedCyclesForMunicipality } from '@/lib/db';
import type {
  AssessmentCycle,
  AssessmentCycleSummary,
  AssessmentCycleStatus,
  CycleUploadResponse,
  CyclePrerequisites,
  ActivateCycleResponse,
  ArchiveCycleResponse,
  ReactivateCycleResponse,
  SubmissionStatus,
  TemplateMapping,
} from '@/types/domain.types';

// Submission summary for cycle listing (matches backend CycleSubmissionResponse)
export interface CycleSubmission {
  id: string;
  rollNumber: string;
  address: string | null;
  status: SubmissionStatus;
  extractedAt: string | null;
  reviewedAt: string | null;
  createdAt: string | null;
  notes?: string | null;
  filename?: string | null; // Original filename for queued/processing submissions
  error?: string | null;
  templateId?: string | null;
  duplicateOf?: string | null;
  contentHash?: string;
  extractionMethod?: string | null;
  reviewRequired?: boolean;
  reviewReasons?: string[];
  diffIncomplete?: boolean;
  camaMatch?: string | null;
  rollCandidates?: { pass1?: string; pass2?: string; chosen?: string; reason?: string } | null;
  // Whether a stored PDF exists — the CAMAlot export requires one, so the RFI
  // tab gates export eligibility on this to match the backend.
  hasPdf?: boolean;
}

export interface CycleSubmissionsResponse {
  submissions: CycleSubmission[];
  total: number;
  page: number;
  limit: number;
  cycleId: string;
  cycleName: string;
}

export interface CycleSubmissionStats {
  queued: number;
  processing: number;
  pending: number;
  reviewed: number;
  rejected: number;
  flagged: number;
  failed: number;
  held: number;
  total: number;
}

export interface CreateCycleRequest {
  name: string;
  year: number;
  templateId?: string;
}

export interface UpdateCycleRequest {
  name?: string;
  year?: number;
  status?: AssessmentCycleStatus;
  templateId?: string;
}

/** Feature 7 (§5.1/§7.4): per-(cycle, type) inspection-round info. */
export interface InspectionRoundsInfo {
  /** ms-epoch round start per fixed type token (annual/supplementary/global);
   * null = no round ever started. Token keys are single words — untouched by
   * the api-client camelCase transform (NOT a data-keyed dict). */
  rounds: Record<string, number | null>;
  /** Type tokens with stamped flag membership in this cycle (wire: types_present). */
  typesPresent: string[];
}

/**
 * Assessment cycles service with AbortController support for all async operations
 */
export const assessmentCyclesService = {
  /**
   * List all assessment cycles for a municipality
   */
  getByMunicipality: async (
    municipalityId: string,
    options?: { signal?: AbortSignal; limit?: number; offset?: number }
  ): Promise<AssessmentCycleSummary[]> => {
    try {
      const data = await get<AssessmentCycleSummary[]>(
        `/municipalities/${municipalityId}/assessment-cycles`,
        {
          params: {
            limit: options?.limit,
            offset: options?.offset,
          },
          signal: options?.signal,
        }
      );
      // Cache each cycle for offline use (fire-and-forget)
      for (const cycle of data) {
        cacheCycle(cycle.id, municipalityId, cycle.name, cycle.year, cycle.status).catch(() => {});
      }
      return data;
    } catch (err) {
      // Don't fall back to cache if the request was intentionally cancelled
      if (options?.signal?.aborted) {
        throw err;
      }
      // Try cache on API failure (offline mode)
      const cached = await getCachedCyclesForMunicipality(municipalityId);
      if (cached.length > 0) {
        return cached.map((c) => ({
          id: c.id,
          name: c.name,
          year: c.year,
          status: c.status as AssessmentCycleStatus,
          propertiesCount: 0,
          createdAt: '',
        }));
      }
      throw err;
    }
  },

  /**
   * Get a specific assessment cycle
   * Caches result for offline use, falls back to cache if API fails
   */
  getById: async (
    municipalityId: string,
    cycleId: string,
    options?: { signal?: AbortSignal }
  ): Promise<AssessmentCycle> => {
    try {
      const data = await get<AssessmentCycle>(
        `/municipalities/${municipalityId}/assessment-cycles/${cycleId}`,
        { signal: options?.signal }
      );
      // Cache for offline use
      await cacheCycle(data.id, municipalityId, data.name, data.year, data.status).catch(() => {
        // Ignore cache errors - non-critical
      });
      return data;
    } catch (err) {
      // Try cache on API failure (offline mode)
      const cached = await getCachedCycle(cycleId).catch(() => null);
      if (cached) {
        return {
          id: cached.id,
          municipalityId: cached.municipalityId,
          name: cached.name,
          year: cached.year,
          status: cached.status as AssessmentCycleStatus,
          createdAt: '',
          updatedAt: '',
        } as AssessmentCycle;
      }
      throw err;
    }
  },

  /**
   * Create a new assessment cycle
   */
  create: async (
    municipalityId: string,
    data: CreateCycleRequest,
    options?: { signal?: AbortSignal }
  ): Promise<{ id: string; name: string; year: number; status: string; message: string }> => {
    return post(
      `/municipalities/${municipalityId}/assessment-cycles`,
      {
        name: data.name,
        year: data.year,
        template_id: data.templateId,
      },
      { signal: options?.signal }
    );
  },

  /**
   * Update an assessment cycle
   */
  update: async (
    municipalityId: string,
    cycleId: string,
    data: UpdateCycleRequest,
    options?: { signal?: AbortSignal }
  ): Promise<{ message: string; id: string }> => {
    return put<{ message: string; id: string }>(
      `/municipalities/${municipalityId}/assessment-cycles/${cycleId}`,
      {
        name: data.name,
        year: data.year,
        status: data.status,
        template_id: data.templateId,
      },
      { signal: options?.signal }
    );
  },

  /**
   * Delete an assessment cycle
   */
  delete: async (
    municipalityId: string,
    cycleId: string,
    options?: { signal?: AbortSignal }
  ): Promise<{ message: string; id: string }> => {
    return del<{ message: string; id: string }>(
      `/municipalities/${municipalityId}/assessment-cycles/${cycleId}`,
      { signal: options?.signal }
    );
  },

  /**
   * Get submissions for an assessment cycle
   */
  getSubmissions: async (
    municipalityId: string,
    cycleId: string,
    options?: {
      signal?: AbortSignal;
      page?: number;
      limit?: number;
      status?: SubmissionStatus;
      search?: string;
    }
  ): Promise<CycleSubmissionsResponse> => {
    return get<CycleSubmissionsResponse>(
      `/municipalities/${municipalityId}/assessment-cycles/${cycleId}/submissions`,
      {
        params: {
          page: options?.page,
          limit: options?.limit,
          status: options?.status,
          search: options?.search || undefined,
        },
        signal: options?.signal,
      }
    );
  },

  /**
   * Get submission statistics for an assessment cycle
   */
  getSubmissionStats: async (
    municipalityId: string,
    cycleId: string,
    options?: { signal?: AbortSignal }
  ): Promise<CycleSubmissionStats> => {
    return get<CycleSubmissionStats>(
      `/municipalities/${municipalityId}/assessment-cycles/${cycleId}/submissions/stats`,
      { signal: options?.signal }
    );
  },

  /**
   * Get building types for a cycle (from imported property data)
   * Used by template mappings to show available building types
   */
  getBuildingTypes: async (
    municipalityId: string,
    cycleId: string,
    options?: { signal?: AbortSignal }
  ): Promise<string[]> => {
    const data = await get<{ buildingTypes: string[] }>(
      `/municipalities/${municipalityId}/assessment-cycles/${cycleId}/building-types`,
      { signal: options?.signal }
    );
    return data.buildingTypes;
  },

  /**
   * Update template mappings for a cycle
   * Maps building types to specific templates for property-type-specific extraction
   */
  updateTemplateMappings: async (
    municipalityId: string,
    cycleId: string,
    mappings: TemplateMapping[],
    options?: { signal?: AbortSignal }
  ): Promise<AssessmentCycle> => {
    return put<AssessmentCycle>(
      `/municipalities/${municipalityId}/assessment-cycles/${cycleId}/template-mappings`,
      { mappings },
      { signal: options?.signal }
    );
  },

  /**
   * Upload inspection PDFs for a cycle
   * Uses FormData for file upload with progress tracking
   */
  uploadInspections: async (
    municipalityId: string,
    cycleId: string,
    files: File[],
    options?: { signal?: AbortSignal; onProgress?: (progress: number) => void }
  ): Promise<CycleUploadResponse> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    // Note: Don't set Content-Type header manually for FormData
    // Axios automatically sets it with the correct boundary
    const response = await apiClient.post<CycleUploadResponse>(
      `/municipalities/${municipalityId}/assessment-cycles/${cycleId}/upload`,
      formData,
      {
        signal: options?.signal,
        timeout: 300000, // 5 minutes - extraction takes time
        onUploadProgress: (progressEvent) => {
          if (options?.onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            options.onProgress(progress);
          }
        },
      }
    );

    return response.data;
  },

  // =========================================================================
  // Lifecycle Methods (Activate / Archive / Reactivate)
  // =========================================================================

  /**
   * Get activation prerequisites for a cycle
   */
  getPrerequisites: async (
    municipalityId: string,
    cycleId: string,
    options?: { signal?: AbortSignal }
  ): Promise<CyclePrerequisites> => {
    return get<CyclePrerequisites>(
      `/municipalities/${municipalityId}/assessment-cycles/${cycleId}/prerequisites`,
      { signal: options?.signal }
    );
  },

  /**
   * Activate a draft cycle (or reactivate an archived one)
   * May auto-archive the currently active cycle
   */
  activate: async (
    municipalityId: string,
    cycleId: string,
    options?: { signal?: AbortSignal }
  ): Promise<ActivateCycleResponse> => {
    return post<ActivateCycleResponse>(
      `/municipalities/${municipalityId}/assessment-cycles/${cycleId}/activate`,
      {},
      { signal: options?.signal }
    );
  },

  /**
   * Archive an active cycle (makes it read-only)
   */
  archive: async (
    municipalityId: string,
    cycleId: string,
    options?: { signal?: AbortSignal }
  ): Promise<ArchiveCycleResponse> => {
    return post<ArchiveCycleResponse>(
      `/municipalities/${municipalityId}/assessment-cycles/${cycleId}/archive`,
      {},
      { signal: options?.signal }
    );
  },

  /**
   * Reactivate an archived cycle
   * May auto-archive the currently active cycle
   */
  reactivate: async (
    municipalityId: string,
    cycleId: string,
    options?: { signal?: AbortSignal }
  ): Promise<ReactivateCycleResponse> => {
    return post<ReactivateCycleResponse>(
      `/municipalities/${municipalityId}/assessment-cycles/${cycleId}/reactivate`,
      {},
      { signal: options?.signal }
    );
  },

  // =========================================================================
  // Inspection rounds (Feature 7 §7.4) — field-permitted, no admin gate.
  // =========================================================================

  /**
   * Round starts + which types have stamped flag membership, for the
   * FieldInspectionTab download flow's reset checkboxes.
   */
  getInspectionRounds: async (
    municipalityId: string,
    cycleId: string,
    options?: { signal?: AbortSignal }
  ): Promise<InspectionRoundsInfo> => {
    return get<InspectionRoundsInfo>(
      `/municipalities/${municipalityId}/assessment-cycles/${cycleId}/inspection-rounds`,
      { signal: options?.signal }
    );
  },

  /**
   * Start a new inspection round for the given type tokens. The interceptor
   * snake_cases the body key (`types`); token VALUES are untouched.
   * Nothing is deleted server-side — only progress-paint cutoffs move.
   */
  resetInspectionRounds: async (
    municipalityId: string,
    cycleId: string,
    types: string[],
    options?: { signal?: AbortSignal }
  ): Promise<{ rounds: Record<string, number | null> }> => {
    return post<{ rounds: Record<string, number | null> }>(
      `/municipalities/${municipalityId}/assessment-cycles/${cycleId}/inspection-rounds/reset`,
      { types },
      { signal: options?.signal }
    );
  },
};

export default assessmentCyclesService;
